import React, { useState, useEffect, useRef } from "react";
import { projectConfiguration } from "./projectConfiguration";
import "./Dashboard.css";

const API_URL = process.env.REACT_APP_API_URL || "";

function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toLocaleString();
}

function formatCurrency(num) {
  return "$" + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatCurrencyOrDash(num) {
  return num == null ? "—" : formatCurrency(num);
}

function PlatformBadge({ platform }) {
  if (!platform) return null;
  const label = platform === "android" ? "Android" : "iOS";
  return <span className={`lb-platform-badge lb-platform-${platform}`}>{label}</span>;
}

function BarChart({ data, height = 40, width = 140 }) {
  if (!data || data.length === 0) return <div className="barchart-empty">--</div>;
  const max = Math.max(...data) || 1;
  const barWidth = Math.max(1, (width - data.length + 1) / data.length);
  const gap = 1;

  return (
    <svg width={width} height={height} className="barchart">
      {data.map((val, i) => {
        const barHeight = Math.max(1, (val / max) * (height - 2));
        const x = i * (barWidth + gap);
        const y = height - barHeight;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barWidth}
            height={barHeight}
            rx={1}
            fill="#5BA4F5"
            opacity={0.85}
          />
        );
      })}
    </svg>
  );
}

function TrendBadge({ data }) {
  if (!data || data.length < 2) return null;
  const recent = data.slice(-3);
  const older = data.slice(-6, -3);
  if (older.length === 0) return null;
  const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
  const olderAvg = older.reduce((a, b) => a + b, 0) / older.length;
  if (olderAvg === 0) return null;
  const pct = ((recentAvg - olderAvg) / olderAvg * 100).toFixed(0);
  const isUp = pct >= 0;
  return (
    <span className={`lb-trend ${isUp ? "lb-trend-up" : "lb-trend-down"}`}>
      {isUp ? "+" : ""}{pct}%
    </span>
  );
}

function DownloadBar({ downloads, maxDownloads }) {
  const pct = maxDownloads > 0 ? (downloads / maxDownloads) * 100 : 0;
  return (
    <div className="lb-bar-track">
      <div className="lb-bar-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

function Countdown() {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft());

  function getTimeLeft() {
    const deadline = new Date("2026-09-30T23:59:59").getTime();
    const now = Date.now();
    const diff = Math.max(0, deadline - now);
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { days, hours, minutes, seconds };
  }

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="countdown">
      <div className="countdown-label">Deadline</div>
      <div className="countdown-timer">
        <div className="countdown-unit">
          <span className="countdown-value">{timeLeft.days}</span>
          <span className="countdown-desc">days</span>
        </div>
        <span className="countdown-sep">:</span>
        <div className="countdown-unit">
          <span className="countdown-value">{String(timeLeft.hours).padStart(2, "0")}</span>
          <span className="countdown-desc">hrs</span>
        </div>
        <span className="countdown-sep">:</span>
        <div className="countdown-unit">
          <span className="countdown-value">{String(timeLeft.minutes).padStart(2, "0")}</span>
          <span className="countdown-desc">min</span>
        </div>
        <span className="countdown-sep">:</span>
        <div className="countdown-unit">
          <span className="countdown-value">{String(timeLeft.seconds).padStart(2, "0")}</span>
          <span className="countdown-desc">sec</span>
        </div>
      </div>
    </div>
  );
}

function RankMedal({ rank }) {
  const medals = { 1: "gold", 2: "silver", 3: "bronze" };
  const cls = medals[rank] || "default";
  return <div className={`lb-rank lb-rank-${cls}`}>{rank}</div>;
}

export default function Dashboard() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [period, setPeriod] = useState("");
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    fetchApps();
    const interval = setInterval(fetchApps, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  async function fetchApps() {
    try {
      const res = await fetch(`${API_URL}/api/apps`);
      const data = await res.json();
      if (data.success) {
        setApps(data.apps);
        setPeriod(data.period || "");
        setWarnings(data.warnings || []);
        setLastUpdated(new Date());
        setError(null);
      } else {
        setError(data.error || "Failed to load data");
        setWarnings([]);
      }
    } catch (err) {
      setError("Cannot connect to API server. Start the backend with: node server.js");
      setWarnings([]);
    }
    setLoading(false);
  }

  const totalDownloads = apps.reduce((s, a) => s + (a.totalDownloads || 0), 0);
  const totalRevenue = apps.reduce((s, a) => s + a.revenue, 0);
  const maxDownloads = apps.length > 0 ? Math.max(...apps.map(a => a.totalDownloads || 0)) : 0;

  return (
    <div className="dash-wrapper" style={{ fontFamily: projectConfiguration.secondaryFont }}>
      <div className="dash-container">
        <header className="dash-header">
          <div></div>
          <button className="lb-refresh-btn" onClick={fetchApps} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </header>

        <Countdown />

        {error && (
          <div className="lb-error">
            <strong>Setup required:</strong> {error}
            <div className="lb-setup-help">
              <code>ASC_ISSUER_ID=... ASC_KEY_ID=... ASC_VENDOR_NUMBER=... ASC_PRIVATE_KEY_PATH=... node server.js</code>
            </div>
          </div>
        )}

        {warnings.length > 0 && (
          <div className="lb-warning">
            <strong>Numbers may be incomplete:</strong>
            <ul className="lb-warning-list">
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        {!error && !loading && apps.length === 0 && (
          <div className="lb-empty">No apps with sales data found for this period.</div>
        )}

        {apps.length > 0 && (
          <>
            <div className="lb-totals">
              <div className="lb-total-item">
                <span className="lb-total-label">Total Downloads</span>
                <span className="lb-total-value">{formatNumber(totalDownloads)}</span>
              </div>
              <div className="lb-total-divider" />
              <div className="lb-total-item">
                <span className="lb-total-label">Total Revenue</span>
                <span className="lb-total-value">{totalRevenue > 0 ? formatCurrency(totalRevenue) : "$0"}</span>
              </div>
              <div className="lb-total-divider" />
              <div className="lb-total-item">
                <span className="lb-total-label">Apps</span>
                <span className="lb-total-value">{apps.length}</span>
              </div>
            </div>

            <div className="lb-table">
              <div className="lb-table-header">
                <span className="lb-col-rank">#</span>
                <span className="lb-col-app">App</span>
                <span className="lb-col-creator">Creator</span>
                <span className="lb-col-downloads">Downloads</span>
                <span className="lb-col-bar"></span>
                <span></span>
                <span className="lb-col-revenue-gross">Revenue (gross)</span>
                <span className="lb-col-revenue">Revenue (net)</span>
                <span className="lb-col-trend">App Units</span>
              </div>

              {apps.map((app, i) => (
                <div key={app.id} className={`lb-row ${i < 3 ? "lb-row-top" : ""}`}>
                  <span className="lb-col-rank">
                    <RankMedal rank={i + 1} />
                  </span>
                  <span className="lb-col-app">
                    <div className="lb-app-name">
                      {app.name}
                      <PlatformBadge platform={app.platform} />
                    </div>
                    <div className="lb-app-bundle">{app.bundleId}</div>
                  </span>
                  <span className="lb-col-creator">Team</span>
                  <span className="lb-col-downloads">
                    <div className="lb-downloads-amount">{formatNumber(app.totalDownloads || 0)}</div>
                    <TrendBadge data={app.downloadsTrend} />
                  </span>
                  <span className="lb-col-bar">
                    <DownloadBar downloads={app.totalDownloads || 0} maxDownloads={maxDownloads} />
                  </span>
                  <span></span>
                  <span className="lb-col-revenue-gross">
                    <div className="lb-revenue-amount">{formatCurrencyOrDash(app.revenueGross)}</div>
                  </span>
                  <span className="lb-col-revenue">
                    <div className="lb-revenue-amount">{formatCurrency(app.revenue)}</div>
                    {app.purchases > 0 && (
                      <div className="lb-purchase-count">
                        {app.purchases} purchase{app.purchases === 1 ? "" : "s"}
                      </div>
                    )}
                  </span>
                  <span className="lb-col-trend">
                    <BarChart data={app.downloadsTrend} />
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
